import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { TableData } from '@uikit/Tables/types';
import {
  assignmentApi,
  assignmentFilterBodyTypes,
  assignmentSearchRidesParamsType,
  contractorCloseoutStatusType,
} from '@store/rtk/assignment';
import { idType } from '@src/types';
import { defaultValues } from '@component/Assignment/AssignmentFilters/constants';
import { formatUTCToLocal, removeCounty } from '@src/utils';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';
import { detailSegmentDataType } from '@component/Monitoring/MonitoringSegmentDetail/MonitoringSegmentTabs/SegmentDetailSegment/constants';
import { normalizeAssignmentLockRows } from '@component/Assignment/AssignmentGrid/AssignmentLockGrid.helpers';

const defaultAssignmentFilters: assignmentFilterBodyTypes = {
  rideRequestDate: defaultValues.rideRequestDate,
  startDate: defaultValues.startDate,
  endDate: defaultValues.endDate,
  includeIncompleteRides: false,
  onlyWithRides: false,
  state: null,
};

const getSelectedRideIds = (rows: TableData['rows'] = []): Array<idType> =>
  rows.reduce<Array<idType>>((ids, row) => {
    if (row.checked && row.rideId !== undefined && row.rideId !== null) {
      ids.push(row.rideId);
    }

    return ids;
  }, []);

const getAssignmentIdKey = (id: idType) => String(id);

const getAssignmentRideKey = (row: Record<string, any>) =>
  String(row.rideId ?? row.rideOrderConfirmationCode ?? row.ride ?? row.id);

const isStandingOrderAssignmentRow = (row: Record<string, any>) =>
  row.standingOrder === true ||
  row.stRadio === true ||
  Boolean(row.standingOrderId);

type AssignedRideItem = {
  id: idType;
  member: string;
  isRestricted: boolean;
  rideNumber: string;
  sourceContractorId?: idType;
  originalRow?: Record<string, any>;
  originalIndex?: number;
};

export type AssignmentModeType = 'ride' | 'standingOrder' | null;

type AssignmentSliceType = {
  assignmentContractorsList: Array<{
    id: idType;
    name: string;
    count: number;
    type?: string;
    restrictedContractorPreference?: boolean;
    hasHiddenRides: boolean;
    locked?: boolean;
    accepted?: boolean;
  }>;
  assignmentTableData: TableData;
  assignmentHistoryTableData: TableData;
  assignmentTransmitTableData: TableData;
  assignmentLockTableData: TableData;
  assignmentRidesLoading: boolean;
  assignmentHistoryLoading: boolean;
  assignmentHistoryActiveContractorId: idType | null;
  assignmentTransmitLoading: boolean;
  assignmentLockLoading: boolean;
  assignmentLockRequestId: string | null;
  assignmentLockContext: string;
  massTransmitEnabled: boolean;
  massTransmitEligibilityLoading: boolean;
  massTransmitEligibilityRequestId: string | null;
  assignmentFilters: assignmentFilterBodyTypes;
  segmentDetailData: any;
  rideOrderId: string;
  selectedContractorName: string;
  selectedRideIds: Array<idType>;
  isRide: boolean;
  isSearchAll: boolean;
  assignModeTrigger: boolean;
  assignIsPressed: boolean;
  assignmentModeType: AssignmentModeType;
  onlyWithRides: boolean;
  assignedData: Record<string, Array<AssignedRideItem>>;
  searchQuery: string;
  rideQuery: string;
  queryParams: Record<string, any>;
  currentTab: string;
  contractorCloseOutInfo: contractorCloseoutStatusType | null;
  expectedSegmentUnlocks: number[];
};

const getAssignmentSelectionMeta = (
  state: AssignmentSliceType,
  rows: Array<Record<string, any>>,
  page: number
) => {
  const checkedRowIds = new Set<string>();
  const checkedRideKeys = new Set<string>();

  state.assignmentTableData.rows.forEach((row) => {
    if (!row.checked) {
      return;
    }

    checkedRowIds.add(getAssignmentIdKey(row.id));
    checkedRideKeys.add(getAssignmentRideKey(row));
  });
  const firstRideKey = rows[0] ? getAssignmentRideKey(rows[0]) : null;
  const hasMatchedExistingSelection = rows.some((row) =>
    state.isRide
      ? checkedRideKeys.has(getAssignmentRideKey(row))
      : checkedRowIds.has(getAssignmentIdKey(row.id))
  );

  return {
    isSelected: (row: Record<string, any>, index: number) =>
      (state.isRide
        ? checkedRideKeys.has(getAssignmentRideKey(row))
        : checkedRowIds.has(getAssignmentIdKey(row.id))) ||
      (!state.assignIsPressed &&
        !hasMatchedExistingSelection &&
        page === 0 &&
        (state.isRide
          ? getAssignmentRideKey(row) === firstRideKey
          : index === 0)),
  };
};

const {
  assignmentTableColumns,
  assignmentHistoryColumns,
  transmitHistoryColumns,
  assignmentLockColumns,
} = TABLE_COLUMNS;

const payerColumn = {
  field: 'payer',
  headerName: 'Payer',
  type: 'string' as const,
  width: '14%',
  isFilterable: true,
  sortingField: 'payer',
  isSortable: true,
  isChecked: true,
  isLocked: false,
};

const marketplaceAssignmentTableColumns = [
  ...assignmentTableColumns.slice(0, -1),
  payerColumn,
  ...assignmentTableColumns.slice(-1),
];

const contractorPortalAssignmentHistoryColumns = [
  ...assignmentHistoryColumns.slice(0, -1),
  payerColumn,
  ...assignmentHistoryColumns.slice(-1),
];

const initialState: AssignmentSliceType = {
  assignmentContractorsList: [],
  assignmentTableData: {
    columns: assignmentTableColumns,
    rows: [],
    size: 20,
    page: 0,
    totalElements: 0,
    totalPages: 0,
    last: true,
    filter: {},
    sortDir: 'asc',
    sortBy: 'ride',
    table: 'assignmentGrid',
  },
  assignmentHistoryTableData: {
    columns: assignmentHistoryColumns,
    rows: [],
    size: 20,
    page: 0,
    totalElements: 0,
    totalPages: 0,
    last: true,
    filter: {},
    sortDir: 'desc',
    sortBy: 'dos',
    table: 'assignmentHistory',
  },
  assignmentTransmitTableData: {
    columns: transmitHistoryColumns,
    rows: [],
    size: 20,
    page: 0,
    totalElements: 0,
    totalPages: 0,
    last: true,
    filter: {},
    sortDir: 'asc',
    sortBy: 'ride',
    table: 'assignmentTransmit',
  },
  assignmentLockTableData: {
    columns: assignmentLockColumns,
    rows: [],
    size: 20,
    page: 0,
    totalElements: 0,
    totalPages: 0,
    last: true,
    filter: {},
    sortDir: 'asc',
    sortBy: 'ride',
    table: 'assignmentLock',
  },
  assignmentRidesLoading: false,
  assignmentHistoryLoading: false,
  assignmentHistoryActiveContractorId: null,
  assignmentTransmitLoading: false,
  assignmentLockLoading: false,
  assignmentLockRequestId: null,
  assignmentLockContext: '',
  massTransmitEnabled: false,
  massTransmitEligibilityLoading: false,
  massTransmitEligibilityRequestId: null,
  assignmentFilters: defaultAssignmentFilters,
  segmentDetailData: null,
  rideOrderId: '',
  selectedContractorName: 'Assignment Grid',
  selectedRideIds: [],
  isRide: true,
  onlyWithRides: false,
  assignModeTrigger: false,
  assignIsPressed: false,
  assignmentModeType: null,
  assignedData: {},
  searchQuery: '',
  rideQuery: '',
  isSearchAll: false,
  contractorCloseOutInfo: null,
  expectedSegmentUnlocks: [],
  queryParams: {
    size: 20,
    page: 0,
    sortDir: 'asc',
    sortBy: 'rideId',
  },
  currentTab: 'grid',
};

export const assignmentSlice = createSlice({
  name: 'assignment',
  initialState,
  reducers: {
    setSegmentChecked: (
      state: AssignmentSliceType,
      {
        payload,
      }: {
        payload: { id: idType; value: boolean };
      }
    ) => {
      if (payload) {
        state.assignmentTableData.rows = state.assignmentTableData.rows.map(
          (el) => {
            if (el.id === payload.id) {
              return {
                ...el,
                checked: payload.value,
              };
            }
            return el;
          }
        );
        state.selectedRideIds = getSelectedRideIds(
          state.assignmentTableData.rows
        );
      }
    },
    setSelectedSegments: (
      state: AssignmentSliceType,
      { payload }: { payload: { ids: Array<idType> } }
    ) => {
      const selectedIds = new Set(payload.ids);

      state.assignmentTableData.rows = state.assignmentTableData.rows.map(
        (row) => ({
          ...row,
          checked: selectedIds.has(row.id),
        })
      );
      state.selectedRideIds = getSelectedRideIds(
        state.assignmentTableData.rows
      );
    },
    setRideOrderId: (
      state: AssignmentSliceType,
      { payload }: { payload: string }
    ) => {
      state.rideOrderId = payload;
    },
    setSelectedContractorName: (
      state: AssignmentSliceType,
      { payload }: { payload: string }
    ) => {
      state.selectedContractorName = payload;
    },
    setSearchQuery: (
      state: AssignmentSliceType,
      { payload }: { payload: string }
    ) => {
      state.searchQuery = payload;
    },
    setRideQuery: (
      state: AssignmentSliceType,
      { payload }: { payload: string }
    ) => {
      state.rideQuery = payload;
    },
    setAssignmentFilters: (
      state: AssignmentSliceType,
      { payload }: { payload: assignmentFilterBodyTypes }
    ) => {
      state.assignmentFilters = payload;
    },
    setQueryParams: (state: AssignmentSliceType, { payload }) => {
      state.queryParams = payload;
    },
    setAssignModeTrigger: (state: AssignmentSliceType, { payload }) => {
      state.assignModeTrigger = payload;
    },
    setCurrentTab: (state: AssignmentSliceType, { payload }) => {
      state.currentTab = payload;
    },
    setAssignIsPressed: (state: AssignmentSliceType, { payload }) => {
      state.assignIsPressed = payload;

      if (!payload) {
        state.assignmentModeType = null;
      }
    },
    setAssignmentModeType: (
      state: AssignmentSliceType,
      { payload }: PayloadAction<AssignmentModeType>
    ) => {
      state.assignmentModeType = payload;
    },
    addExpectedSegmentUnlocks: (
      state: AssignmentSliceType,
      { payload }: PayloadAction<number[]>
    ) => {
      const existing = new Set(state.expectedSegmentUnlocks);
      payload.forEach((id) => existing.add(id));
      state.expectedSegmentUnlocks = Array.from(existing);
    },
    removeExpectedSegmentUnlocks: (
      state: AssignmentSliceType,
      { payload }: PayloadAction<number[]>
    ) => {
      const toRemove = new Set(payload);
      state.expectedSegmentUnlocks = state.expectedSegmentUnlocks.filter(
        (id) => !toRemove.has(id)
      );
    },
    clearSelectedSegments: (state: AssignmentSliceType) => {
      state.assignmentTableData.rows = state.assignmentTableData.rows.map(
        (row) => ({
          ...row,
          checked: false,
        })
      );
      state.selectedRideIds = [];
    },
    clearAssignmentTableData: (state) => {
      state.assignmentTableData.rows = [];
      state.selectedRideIds = [];
      state.assignmentTableData.page = 0;
      state.assignmentTableData.size = 20;
      state.assignmentTableData.totalElements = 0;
      state.assignmentTableData.totalPages = 0;
      state.assignmentTableData.last = true;
      state.assignmentTableData.sortBy = '';
      state.assignmentTableData.sortDir = 'asc';
    },
    clearAssignmentSegmentDetail: (state) => {
      state.segmentDetailData = null;
    },
    updateAssignmentRideStatus: (
      state: AssignmentSliceType,
      {
        payload,
      }: PayloadAction<{
        segmentId: idType;
        statusId?: idType;
        status?: string;
      }>
    ) => {
      const statusId = payload.statusId;

      state.assignmentTableData.rows = state.assignmentTableData.rows.map(
        (row) =>
          getAssignmentIdKey(row.id) === getAssignmentIdKey(payload.segmentId)
            ? {
                ...row,
                status: payload.status ?? row.status,
                statusId: statusId ?? row.statusId,
              }
            : row
      );

      if (
        state.segmentDetailData &&
        getAssignmentIdKey(state.segmentDetailData.id) ===
          getAssignmentIdKey(payload.segmentId)
      ) {
        const currentSegmentDetail = state.segmentDetailData as
          | (detailSegmentDataType & { statusId?: idType })
          | null;

        state.segmentDetailData = {
          ...currentSegmentDetail,
          status: payload.status ?? currentSegmentDetail?.status,
          statusId: statusId ?? currentSegmentDetail?.statusId,
        } as detailSegmentDataType;
      }
    },
    updateAssignmentWillCallPickupTime: (
      state: AssignmentSliceType,
      { payload }: PayloadAction<{ segmentId: number; pickupTime: string }>
    ) => {
      state.assignmentTableData.rows = state.assignmentTableData.rows.map(
        (row) =>
          getAssignmentIdKey(row.id) === getAssignmentIdKey(payload.segmentId)
            ? {
                ...row,
                pickUpTime: payload.pickupTime,
                departs: payload.pickupTime,
              }
            : row
      );
    },
    assignRide: (state, { payload }) => {
      const {
        contractorId,
        sourceContractorId = -1,
        data,
        keepInGrid = false,
      } = payload;
      const nextItems: Array<AssignedRideItem> = Array.isArray(data)
        ? data
            .map((item) => {
              const originalIndex = state.assignmentTableData.rows.findIndex(
                (row) =>
                  getAssignmentIdKey(row.id) === getAssignmentIdKey(item.id)
              );
              const originalRow =
                originalIndex >= 0
                  ? state.assignmentTableData.rows[originalIndex]
                  : item.originalRow;

              return {
                ...item,
                sourceContractorId,
                originalIndex:
                  originalIndex >= 0 ? originalIndex : item.originalIndex,
                originalRow: originalRow
                  ? {
                      ...originalRow,
                      checked: false,
                    }
                  : undefined,
              };
            })
            .filter((item) => item.id !== null && item.id !== undefined)
        : [];
      const existingItems = state.assignedData[contractorId] || [];
      const existingIds = new Set(
        existingItems.map((item) => getAssignmentIdKey(item.id))
      );
      const itemsToAdd = nextItems.filter(
        (item) => !existingIds.has(getAssignmentIdKey(item.id))
      );
      const delta = itemsToAdd.length;

      if (!delta) {
        return;
      }

      state.assignedData[contractorId] = [...existingItems, ...itemsToAdd];

      state.assignmentContractorsList = state.assignmentContractorsList.map(
        (contractor) => {
          if (contractor.id === contractorId) {
            return {
              ...contractor,
              count:
                contractor.id === sourceContractorId
                  ? contractor.count
                  : contractor.count + delta,
            };
          }

          if (contractor.id === sourceContractorId) {
            return {
              ...contractor,
              count: Math.max(0, contractor.count - delta),
            };
          }

          return contractor;
        }
      );

      state.assignmentTableData.rows = state.assignmentTableData.rows.map(
        (el) =>
          itemsToAdd
            .map((elem) => getAssignmentIdKey(elem.id))
            .includes(getAssignmentIdKey(el.id))
            ? {
                ...el,
                checked: keepInGrid ? el.checked : false,
              }
            : el
      );

      if (!keepInGrid && sourceContractorId !== contractorId) {
        const assignedIds = new Set(
          itemsToAdd.map((elem) => getAssignmentIdKey(elem.id))
        );
        state.assignmentTableData.rows = state.assignmentTableData.rows.filter(
          (row) => !assignedIds.has(getAssignmentIdKey(row.id))
        );
        state.assignmentTableData.totalElements = Math.max(
          0,
          (state.assignmentTableData.totalElements ?? 0) - delta
        );
      }

      state.selectedRideIds = getSelectedRideIds(
        state.assignmentTableData.rows
      );
    },
    unAssignRide: (state, { payload }) => {
      const { contractorId, rowId } = payload;
      const targetRide = state.assignedData[contractorId]?.find(
        (el) => getAssignmentIdKey(el.id) === getAssignmentIdKey(rowId)
      );

      state.assignedData = {
        ...state.assignedData,
        [contractorId]: state.assignedData[contractorId].filter(
          (el) => getAssignmentIdKey(el.id) !== getAssignmentIdKey(rowId)
        ),
      };

      if (targetRide) {
        state.assignmentContractorsList = state.assignmentContractorsList.map(
          (contractor) => {
            if (targetRide.sourceContractorId === contractorId) {
              return contractor;
            }

            if (contractor.id === contractorId) {
              return {
                ...contractor,
                count: Math.max(0, contractor.count - 1),
              };
            }

            if (contractor.id === -1) {
              return {
                ...contractor,
                count:
                  targetRide.sourceContractorId === contractor.id
                    ? contractor.count + 1
                    : contractor.count,
              };
            }

            if (contractor.id === targetRide.sourceContractorId) {
              return {
                ...contractor,
                count: contractor.count + 1,
              };
            }

            return contractor;
          }
        );

        if (
          targetRide.sourceContractorId !== contractorId &&
          targetRide.originalRow &&
          !state.assignmentTableData.rows.some(
            (row) => getAssignmentIdKey(row.id) === getAssignmentIdKey(rowId)
          )
        ) {
          const nextRows = [...state.assignmentTableData.rows];
          const insertIndex =
            typeof targetRide.originalIndex === 'number'
              ? Math.min(targetRide.originalIndex, nextRows.length)
              : nextRows.length;

          nextRows.splice(insertIndex, 0, targetRide.originalRow);
          state.assignmentTableData.rows = nextRows;
          state.assignmentTableData.totalElements =
            (state.assignmentTableData.totalElements ?? 0) + 1;
        }

        state.selectedRideIds = getSelectedRideIds(
          state.assignmentTableData.rows
        );
      }
    },
    clearAssignedData: (
      state,
      { payload }: PayloadAction<{ rollback?: boolean } | undefined>
    ) => {
      const shouldRollback = payload?.rollback ?? true;

      if (!shouldRollback) {
        state.assignedData = {};
        return;
      }

      Object.entries(state.assignedData).forEach(([contractorId, rides]) => {
        const targetContractorId = contractorId;

        rides.forEach((ride) => {
          if (String(ride.sourceContractorId) === targetContractorId) {
            return;
          }

          state.assignmentContractorsList = state.assignmentContractorsList.map(
            (contractor) => {
              if (String(contractor.id) === targetContractorId) {
                return {
                  ...contractor,
                  count: Math.max(0, contractor.count - 1),
                };
              }

              if (contractor.id === ride.sourceContractorId) {
                return {
                  ...contractor,
                  count: contractor.count + 1,
                };
              }

              return contractor;
            }
          );

          if (
            String(ride.sourceContractorId) !== targetContractorId &&
            ride.originalRow &&
            !state.assignmentTableData.rows.some(
              (row) =>
                getAssignmentIdKey(row.id) === getAssignmentIdKey(ride.id)
            )
          ) {
            const nextRows = [...state.assignmentTableData.rows];
            const insertIndex =
              typeof ride.originalIndex === 'number'
                ? Math.min(ride.originalIndex, nextRows.length)
                : nextRows.length;

            nextRows.splice(insertIndex, 0, ride.originalRow);
            state.assignmentTableData.rows = nextRows;
            state.assignmentTableData.totalElements =
              (state.assignmentTableData.totalElements ?? 0) + 1;
          }
        });
      });
      state.assignedData = {};
      state.selectedRideIds = getSelectedRideIds(
        state.assignmentTableData.rows
      );
    },
    setIsRide: (
      state,
      {
        payload,
      }: PayloadAction<boolean | { value: boolean; selectedSegment: idType }>
    ) => {
      const nextIsRide = typeof payload === 'boolean' ? payload : payload.value;

      state.isRide = nextIsRide;

      if (typeof payload === 'boolean') {
        return;
      }

      const rows = state.assignmentTableData.rows;
      const disabledRowIds = new Set<string>();

      if (
        state.assignIsPressed &&
        state.assignmentModeType === 'standingOrder'
      ) {
        rows.forEach((row) => {
          if (!isStandingOrderAssignmentRow(row)) {
            disabledRowIds.add(String(row.id));
          }
        });
      } else if (!state.assignIsPressed) {
        Object.values(state.assignedData).forEach((rides) => {
          rides.forEach((ride) => disabledRowIds.add(String(ride.id)));
        });
      }

      const selectableRows = rows.filter(
        (row) => !disabledRowIds.has(String(row.id))
      );
      const activeRow =
        selectableRows.find(
          (row) => String(row.id) === String(payload.selectedSegment)
        ) ||
        selectableRows.find((row) => row.checked) ||
        selectableRows[0];

      if (!activeRow) {
        state.assignmentTableData.rows = rows.map((row) => ({
          ...row,
          checked: false,
        }));
        state.selectedRideIds = [];
        return;
      }

      const activeRideKey = getAssignmentRideKey(activeRow);
      const selectedIds = new Set<idType>();

      if (nextIsRide) {
        selectableRows.forEach((row) => {
          if (getAssignmentRideKey(row) === activeRideKey) {
            selectedIds.add(row.id);
          }
        });
      } else {
        selectedIds.add(activeRow.id);
      }

      state.assignmentTableData.rows = rows.map((row) => ({
        ...row,
        checked: selectedIds.has(row.id),
      }));
      state.selectedRideIds = getSelectedRideIds(
        state.assignmentTableData.rows
      );
    },
    setIsSearchAll: (state, { payload }: { payload: boolean }) => {
      state.isSearchAll = payload;
    },
    setOnlyWithRides: (state, { payload }: { payload: boolean }) => {
      state.onlyWithRides = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        assignmentApi.endpoints.searchAssignmentRides.matchPending,
        assignmentApi.endpoints.searchContractorPortalMarketplaceRides
          .matchPending,
        assignmentApi.endpoints.searchAssignmentTransmitRides.matchPending
      ),
      (state, { meta }) => {
        if (
          meta.arg.endpointName === 'searchAssignmentTransmitRides' &&
          !('body' in meta.arg.originalArgs)
        ) {
          return;
        }

        const requestArgs = meta.arg.originalArgs as {
          params?: assignmentSearchRidesParamsType;
        };
        const page = requestArgs.params?.page ?? 0;
        state.assignmentRidesLoading = page === 0;
      }
    );
    builder.addMatcher(
      isAnyOf(
        assignmentApi.endpoints.searchAssignmentRides.matchFulfilled,
        assignmentApi.endpoints.searchContractorPortalMarketplaceRides
          .matchFulfilled,
        assignmentApi.endpoints.searchAssignmentTransmitRides.matchFulfilled
      ),
      (state, { payload, meta }) => {
        if (
          meta.arg.endpointName === 'searchAssignmentTransmitRides' &&
          !('body' in meta.arg.originalArgs)
        ) {
          return;
        }

        state.assignmentRidesLoading = false;

        if (payload) {
          const requestArgs = meta.arg.originalArgs as {
            params?: assignmentSearchRidesParamsType;
          };
          const requestParams = requestArgs.params || {};

          let rideOrderConfirmationCode = '';
          let segmentNumber = 0;
          const selection = getAssignmentSelectionMeta(
            state,
            payload.content,
            payload.page
          );

          const newRows = payload.content.map((el, index) => {
            if (el.rideOrderConfirmationCode === rideOrderConfirmationCode) {
              segmentNumber += 1;
            } else {
              rideOrderConfirmationCode = el.rideOrderConfirmationCode;
              segmentNumber = 1;
            }

            return {
              ...el,
              stRadio: el.standingOrder,
              departs: el.pickUpTime,
              country: removeCounty(el.pickUpCounty),
              puCity: el.pickUpCityZipcode,
              doCity: el.dropOfCityZipcode,
              miles: el.miles,
              duration: el.duration,
              ride: el.rideOrderConfirmationCode,
              los: el.los,
              member: el.member,
              service: el.service,
              segment: segmentNumber,
              checked: selection.isSelected(el, index),
            };
          });

          state.assignmentTableData = {
            ...state.assignmentTableData,
            columns:
              meta.arg.endpointName ===
                'searchContractorPortalMarketplaceRides' ||
              ('isContractorPortal' in meta.arg.originalArgs &&
                meta.arg.originalArgs.isContractorPortal)
                ? marketplaceAssignmentTableColumns
                : assignmentTableColumns,
            rows:
              payload.page === 0
                ? newRows
                : [
                    ...state.assignmentTableData.rows,
                    ...newRows.filter(
                      (row) =>
                        !state.assignmentTableData.rows.some(
                          (existingRow) => existingRow.id === row.id
                        )
                    ),
                  ],
            page: payload.page,
            size: payload.size,
            totalElements: payload.totalElements,
            totalPages: payload.totalPages,
            last: payload.last,
            filter: payload.filter ?? {},
            sortDir: requestParams.sortDir === 'desc' ? 'desc' : 'asc',
            sortBy:
              typeof requestParams.sortBy === 'string'
                ? requestParams.sortBy
                : '',
            table: payload.table,
            requestTablePageChange:
              meta.arg.endpointName === 'searchAssignmentTransmitRides'
                ? assignmentApi.endpoints.searchAssignmentTransmitRides
                : meta.arg.endpointName ===
                    'searchContractorPortalMarketplaceRides'
                  ? assignmentApi.endpoints
                      .searchContractorPortalMarketplaceRides
                  : assignmentApi.endpoints.searchAssignmentRides,
          };
          state.selectedRideIds = getSelectedRideIds(
            state.assignmentTableData.rows
          );
        }
      }
    );
    builder.addMatcher(
      isAnyOf(
        assignmentApi.endpoints.searchAssignmentRides.matchRejected,
        assignmentApi.endpoints.searchContractorPortalMarketplaceRides
          .matchRejected,
        assignmentApi.endpoints.searchAssignmentTransmitRides.matchRejected
      ),
      (state, { meta }) => {
        if (
          meta.arg.endpointName === 'searchAssignmentTransmitRides' &&
          !('body' in meta.arg.originalArgs)
        ) {
          return;
        }

        const requestArgs = meta.arg.originalArgs as {
          params?: assignmentSearchRidesParamsType;
        };
        const page = requestArgs.params?.page ?? 0;

        if (page === 0) {
          state.assignmentRidesLoading = false;
        }
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.assignCombinedSearchContractors.matchPending,
      (state, { meta }) => {
        const requestedRideQuery = meta.arg.originalArgs?.body?.rideQuery;

        if (!requestedRideQuery && !meta.arg.originalArgs?.skipStoreUpdate) {
          state.massTransmitEnabled = false;
          state.massTransmitEligibilityLoading = false;
          state.massTransmitEligibilityRequestId = null;
        }
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.assignCombinedSearchContractors.matchFulfilled,
      (state, { payload, meta }) => {
        // TODO: need handle this form backend
        const requestedRideQuery = meta.arg.originalArgs?.body?.rideQuery;

        if (requestedRideQuery || meta.arg.originalArgs?.skipStoreUpdate) {
          return;
        }

        state.assignmentContractorsList = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.content)
            ? payload.content
            : [];
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.assignCombinedSearch.matchPending,
      (state, { meta }) => {
        const page = meta.arg.originalArgs?.params?.page ?? 0;
        state.assignmentRidesLoading = page === 0;

        if (!meta.arg.originalArgs?.body?.rideQuery) {
          state.massTransmitEnabled = false;
          state.massTransmitEligibilityLoading = false;
          state.massTransmitEligibilityRequestId = null;
        }
      }
    );

    builder.addMatcher(
      assignmentApi.endpoints.assignCombinedSearch.matchFulfilled,
      (state, { payload, meta }) => {
        state.assignmentRidesLoading = false;
        const { rides, contractors: newContractor } = payload;

        // TODO: need handle this form backend
        const requestedRideQuery = meta.arg.originalArgs?.body?.rideQuery;

        if (!requestedRideQuery) {
          state.assignmentContractorsList = Array.isArray(newContractor)
            ? newContractor
            : Array.isArray(newContractor?.content)
              ? newContractor.content
              : [];
        }

        if (!rides) return;

        const requestParams = meta.arg.originalArgs?.params || {};

        let rideOrderConfirmationCode = '';
        let segmentNumber = 0;

        const selection = getAssignmentSelectionMeta(
          state,
          rides.content,
          rides.page
        );

        const newRows = rides.content.map((el, index) => {
          if (el.rideOrderConfirmationCode === rideOrderConfirmationCode) {
            segmentNumber += 1;
          } else {
            rideOrderConfirmationCode = el.rideOrderConfirmationCode;
            segmentNumber = 1;
          }

          return {
            ...el,
            stRadio: el.standingOrder,
            departs: el.pickUpTime,
            country: el.pickUpCounty,
            puCity: el.pickUpCityZipcode,
            doCity: el.dropOfCityZipcode,
            miles: el.miles,
            duration: el.duration,
            ride: el.rideOrderConfirmationCode,
            los: el.los,
            member: el.member,
            service: el.service,
            segment: segmentNumber,
            checked: selection.isSelected(el, index),
          };
        });

        let rows;
        if (rides.page === 0) {
          rows = newRows;
        } else {
          const existingIds = new Set(
            state.assignmentTableData.rows.map((r) => r.id)
          );
          rows = [
            ...state.assignmentTableData.rows,
            ...newRows.filter((row) => !existingIds.has(row.id)),
          ];
        }

        state.assignmentTableData = {
          ...state.assignmentTableData,
          rows,
          page: rides.page,
          size: rides.size,
          totalElements: rides.totalElements,
          totalPages: rides.totalPages,
          last: rides.last,
          filter: rides.filter ?? {},
          sortDir: requestParams.sortDir === 'desc' ? 'desc' : 'asc',
          sortBy:
            typeof requestParams.sortBy === 'string'
              ? requestParams.sortBy
              : '',
          table: rides.table,
          requestTablePageChange: assignmentApi.endpoints.searchAssignmentRides,
        };

        state.selectedRideIds = getSelectedRideIds(
          state.assignmentTableData.rows
        );
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.assignCombinedSearch.matchRejected,
      (state, { meta }) => {
        const page = meta.arg.originalArgs?.params?.page ?? 0;

        if (page === 0) {
          state.assignmentRidesLoading = false;
        }
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getContractorsWithNonTransmittedSegments
        .matchPending,
      (state, { meta }) => {
        state.massTransmitEnabled = false;
        state.massTransmitEligibilityLoading = true;
        state.massTransmitEligibilityRequestId = meta.requestId;
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getContractorsWithNonTransmittedSegments
        .matchFulfilled,
      (state, { payload, meta }) => {
        if (state.massTransmitEligibilityRequestId !== meta.requestId) {
          return;
        }

        state.massTransmitEnabled = payload;
        state.massTransmitEligibilityLoading = false;
        state.massTransmitEligibilityRequestId = null;
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getContractorsWithNonTransmittedSegments
        .matchRejected,
      (state, { meta }) => {
        if (state.massTransmitEligibilityRequestId !== meta.requestId) {
          return;
        }

        state.massTransmitEnabled = false;
        state.massTransmitEligibilityLoading = false;
        state.massTransmitEligibilityRequestId = null;
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getAssignmentHistory.matchPending,
      (state, { meta }) => {
        const page = meta.arg.originalArgs?.params?.page ?? 0;
        state.assignmentHistoryLoading = page === 0;
        state.assignmentHistoryActiveContractorId =
          meta.arg.originalArgs?.contractorId ?? null;
      }
    );

    builder.addMatcher(
      assignmentApi.endpoints.getAssignmentHistory.matchFulfilled,
      (state, { payload, meta }) => {
        if (
          meta.arg.originalArgs?.contractorId !==
          state.assignmentHistoryActiveContractorId
        ) {
          return;
        }

        state.assignmentHistoryLoading = false;

        const requestParams = meta.arg.originalArgs?.params || {};
        const page = requestParams.page ?? 0;

        const formattedRows = payload.content.map((el) => ({
          ...el,
          dos: formatUTCToLocal(el.dos),
        }));

        state.assignmentHistoryTableData = {
          ...state.assignmentHistoryTableData,
          columns: meta.arg.originalArgs?.isContractorPortal
            ? contractorPortalAssignmentHistoryColumns
            : assignmentHistoryColumns,
          rows:
            page === 0
              ? formattedRows
              : [
                  ...(state.assignmentHistoryTableData.rows || []),
                  ...formattedRows,
                ],
          page: payload.page ?? 0,
          size: payload.size ?? 10,
          totalElements: payload.totalElements ?? 0,
          totalPages: payload.totalPages ?? 0,
          last: Boolean(payload.last),
          filter: payload.filter ?? {},
          sortDir: requestParams.sortDir === 'desc' ? 'desc' : 'asc',
          sortBy:
            typeof requestParams.sortBy === 'string'
              ? requestParams.sortBy
              : '',
          table: payload.table,
          requestTablePageChange: assignmentApi.endpoints.getAssignmentHistory,
        };
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getAssignmentHistory.matchRejected,
      (state, { meta }) => {
        if (
          meta.arg.originalArgs?.contractorId !==
          state.assignmentHistoryActiveContractorId
        ) {
          return;
        }

        const page = meta.arg.originalArgs?.params?.page ?? 0;

        if (page === 0) {
          state.assignmentHistoryLoading = false;
        }
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getTransmitHistory.matchPending,
      (state, { meta }) => {
        const page = meta.arg.originalArgs?.params?.page ?? 0;
        state.assignmentTransmitLoading = page === 0;
      }
    );

    builder.addMatcher(
      assignmentApi.endpoints.getTransmitHistory.matchFulfilled,
      (state, { payload, meta }) => {
        state.assignmentTransmitLoading = false;

        const requestParams = meta.arg.originalArgs?.params || {};
        const page = requestParams.page ?? 0;

        state.assignmentTransmitTableData = {
          ...state.assignmentTransmitTableData,
          rows:
            page === 0
              ? payload.content.map((el) => ({
                  ...el,
                  dateTime: formatUTCToLocal(el.dateTime),
                }))
              : [
                  ...(state.assignmentTransmitTableData.rows || []),
                  ...payload.content.map((el) => ({
                    ...el,
                    dateTime: formatUTCToLocal(el.dateTime),
                  })),
                ],
          page: payload.page ?? 0,
          size: payload.size ?? 10,
          totalElements: payload.totalElements ?? 0,
          totalPages: payload.totalPages ?? 0,
          last: Boolean(payload.last),
          filter: payload.filter ?? {},
          sortDir: requestParams.sortDir === 'desc' ? 'desc' : 'asc',
          sortBy:
            typeof requestParams.sortBy === 'string'
              ? requestParams.sortBy
              : '',
          table: payload.table,
          requestTablePageChange: assignmentApi.endpoints.getTransmitHistory,
        };
      }
    );

    builder.addMatcher(
      assignmentApi.endpoints.getTransmitHistory.matchRejected,
      (state, { meta }) => {
        const page = meta.arg.originalArgs?.params?.page ?? 0;

        if (page === 0) {
          state.assignmentTransmitLoading = false;
        }
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getAssignmentLockHistory.matchPending,
      (state, { meta }) => {
        const page = meta.arg.originalArgs?.params?.page ?? 0;
        state.assignmentLockLoading = page === 0;
        if (page === 0) {
          state.assignmentLockRequestId = meta.requestId;
          state.assignmentLockContext = `${meta.arg.originalArgs.contractorId}:${meta.arg.originalArgs.params?.dateOfService}`;
          state.assignmentLockTableData.rows = [];
        }
      }
    );

    builder.addMatcher(
      assignmentApi.endpoints.getAssignmentLockHistory.matchFulfilled,
      (state, { payload, meta }) => {
        if (
          `${meta.arg.originalArgs.contractorId}:${meta.arg.originalArgs.params?.dateOfService}` !==
          state.assignmentLockContext
        )
          return;
        if (
          (meta.arg.originalArgs?.params?.page ?? 0) === 0 &&
          meta.requestId !== state.assignmentLockRequestId
        )
          return;
        state.assignmentLockLoading = false;

        const requestParams = meta.arg.originalArgs?.params || {};
        const page = requestParams.page ?? 0;

        const normalizedRows = normalizeAssignmentLockRows(payload.content);

        state.assignmentLockTableData = {
          ...state.assignmentLockTableData,
          columns: meta.arg.originalArgs.isContractorPortal
            ? [
                ...assignmentLockColumns.slice(0, 2),
                payerColumn,
                ...assignmentLockColumns.slice(2),
              ]
            : assignmentLockColumns,
          rows:
            page === 0
              ? normalizedRows
              : [
                  ...(state.assignmentLockTableData.rows || []),
                  ...normalizedRows,
                ],
          page: payload.page ?? 0,
          size: payload.size ?? 10,
          totalElements: payload.totalElements ?? 0,
          totalPages: payload.totalPages ?? 0,
          last: Boolean(payload.last),
          filter: payload.filter ?? {},
          sortDir: requestParams.sortDir === 'desc' ? 'desc' : 'asc',
          sortBy:
            typeof requestParams.sortBy === 'string'
              ? requestParams.sortBy
              : '',
          table: payload.table,
          requestTablePageChange:
            assignmentApi.endpoints.getAssignmentLockHistory,
        };
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getAssignmentLockHistory.matchRejected,
      (state, { meta }) => {
        const page = meta.arg.originalArgs?.params?.page ?? 0;

        if (page === 0 && meta.requestId === state.assignmentLockRequestId) {
          state.assignmentLockLoading = false;
        }
      }
    );
    builder.addMatcher(
      assignmentApi.endpoints.getContractorCloseoutStatus.matchFulfilled,
      (state, { payload }) => {
        state.contractorCloseOutInfo = payload;
      }
    );
  },
});

export const {
  setSegmentChecked,
  setSelectedSegments,
  setQueryParams,
  setCurrentTab,
  setRideOrderId,
  setSelectedContractorName,
  setAssignmentFilters,
  clearSelectedSegments,
  clearAssignmentTableData,
  clearAssignmentSegmentDetail,
  updateAssignmentRideStatus,
  updateAssignmentWillCallPickupTime,
  assignRide,
  unAssignRide,
  setIsRide,
  setIsSearchAll,
  setOnlyWithRides,
  clearAssignedData,
  setSearchQuery,
  setRideQuery,
  setAssignModeTrigger,
  setAssignIsPressed,
  setAssignmentModeType,
  addExpectedSegmentUnlocks,
  removeExpectedSegmentUnlocks,
} = assignmentSlice.actions;

export const selectAssignment = (state: { assignment: AssignmentSliceType }) =>
  state.assignment;

export const assignmentReducer = assignmentSlice.reducer;
